import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Platform } from 'react-native';
import { XStack, YStack } from 'tamagui';
import { Text } from '../primitives/Text';
import { Button } from '../primitives/Button';
import { AdaptivePanel } from './AdaptivePanel';
import { hapticsRaw } from '../utils/haptics';

export type ConfirmTone = 'default' | 'danger' | 'warning';

export interface ConfirmRequest {
  /** Dialog title — the assertive question being asked. */
  title: string;
  /** Body copy. Optional but encouraged for destructive actions. */
  message?: string;
  /** Label on the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label on the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * Visual tone.
   *  - 'default' (default): system Alert.alert on iOS/Android, panel on web.
   *  - 'danger': red confirm button on all platforms — falls back to the
   *    custom panel even on native because Alert.alert doesn't expose a
   *    destructive style here.
   *  - 'warning': amber confirm button.
   */
  tone?: ConfirmTone;
}

type ConfirmFn = (req: ConfirmRequest) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Mount once near the app root (inside any Theme / SafeAreaProvider).
 * Provides the `useConfirm` hook so any descendant can pop a
 * destructive-confirmation dialog without prop-drilling its own state.
 *
 * Per ADR-0077 the dialog inherits AdaptivePanel's focus-trap +
 * Escape-dismiss behaviour. iOS / Android default-tone confirmations
 * fall back to native Alert.alert so the OS chrome stays familiar;
 * the panel renders only when tone='danger' / 'warning' needs custom
 * styling.
 */
export interface ConfirmProviderProps {
  children: React.ReactNode;
}

interface ActiveRequest extends ConfirmRequest {
  resolve: (v: boolean) => void;
}

export function ConfirmProvider({ children }: ConfirmProviderProps) {
  // `panelActive` drives whether the AdaptivePanel renders. It mirrors
  // the panel-side branch of `activeReqRef`, which is the single source
  // of truth for the in-flight request (covers BOTH the native
  // Alert.alert path and the panel path so overwrite + unmount safety
  // work uniformly).
  const [panelActive, setPanelActive] = useState<ActiveRequest | null>(null);
  const activeReqRef = useRef<ActiveRequest | null>(null);

  const confirm = useCallback<ConfirmFn>((req) => {
    return new Promise<boolean>((resolve) => {
      // Overwrite guard runs first — resolve any in-flight request
      // (native or panel) to false before installing this one. The ref
      // is mutated synchronously so two confirm() calls in the same
      // tick correctly chain (the second sees the first's resolver,
      // not the stale null from the previous render).
      const prev = activeReqRef.current;
      if (prev) prev.resolve(false);

      const next: ActiveRequest = { ...req, resolve };
      activeReqRef.current = next;

      const nativeAlert = Platform.OS !== 'web' && (req.tone ?? 'default') === 'default';
      if (nativeAlert) {
        // Belt-and-suspenders: ensure no panel renders alongside the
        // native alert (would only happen on a same-tick panel→native
        // swap, but cheap to guarantee).
        setPanelActive(null);
        const finalize = (v: boolean) => {
          if (activeReqRef.current === next) activeReqRef.current = null;
          resolve(v);
        };
        Alert.alert(req.title, req.message, [
          { text: req.cancelLabel ?? 'Cancel', style: 'cancel', onPress: () => finalize(false) },
          { text: req.confirmLabel ?? 'Confirm', onPress: () => finalize(true) },
        ]);
        return;
      }
      setPanelActive(next);
    });
  }, []);

  // Unmount-safety: route change / parent re-mount while a request is
  // in flight — resolve to false so the awaiting caller doesn't hang.
  // Covers BOTH paths via the unified ref.
  useEffect(() => {
    return () => {
      const current = activeReqRef.current;
      if (current) {
        current.resolve(false);
        activeReqRef.current = null;
      }
    };
  }, []);

  const handleClose = useCallback(() => {
    const current = activeReqRef.current;
    activeReqRef.current = null;
    setPanelActive(null);
    current?.resolve(false);
  }, []);

  const handleConfirm = useCallback(() => {
    const current = activeReqRef.current;
    // ADR-0079: warning haptic on irreversible confirm. Uses the raw
    // surface to bypass HapticsProvider — destructive feedback should
    // fire even when general haptics are muted (Apple's HIG suggests
    // critical alerts vibrate unconditionally).
    if (current?.tone === 'danger' || current?.tone === 'warning') {
      hapticsRaw.warning();
    }
    activeReqRef.current = null;
    setPanelActive(null);
    current?.resolve(true);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {panelActive ? (
        <AdaptivePanel
          mode="modal"
          open={true}
          onOpenChange={(next) => {
            if (!next) handleClose();
          }}
          title={panelActive.title}
          footer={
            <XStack gap="$2" justifyContent="flex-end">
              <Button variant="ghost" onPress={handleClose}>
                {panelActive.cancelLabel ?? 'Cancel'}
              </Button>
              <Button
                variant={panelActive.tone === 'danger' ? 'danger' : panelActive.tone === 'warning' ? 'warning' : 'primary'}
                onPress={handleConfirm}
              >
                {panelActive.confirmLabel ?? 'Confirm'}
              </Button>
            </XStack>
          }
        >
          {panelActive.message ? (
            <YStack padding="$2">
              <Text variant="body">{panelActive.message}</Text>
            </YStack>
          ) : null}
        </AdaptivePanel>
      ) : null}
    </ConfirmContext.Provider>
  );
}

/**
 * Returns a function that pops a confirmation dialog and resolves to
 * `true` on confirm, `false` on cancel/dismiss. Throws if no
 * `ConfirmProvider` is mounted upstream — fail loudly so call sites
 * notice rather than silently swallowing confirms.
 *
 * Example:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete trip?',
 *     message: 'This cannot be undone.',
 *     confirmLabel: 'Delete',
 *     tone: 'danger',
 *   });
 *   if (!ok) return;
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  // Stable reference if mounted; fallback throws when called.
  return useMemo<ConfirmFn>(() => {
    if (ctx) return ctx;
    return () => {
      throw new Error(
        '[useConfirm] No <ConfirmProvider> mounted. Wrap your app in <ConfirmProvider> near the root.',
      );
    };
  }, [ctx]);
}
